import {
  DEFAULT_RESPONSIVE_TEXT_SIZE,
  responsiveTextSizeDefaults,
  ResponsiveTextSize,
  TextSize,
} from "./text-sizes";
import geniusfeedJson from "./themes/geniusfeed.json";
import pixupJson from "./themes/pixup.json";
import sportsbookcoreJson from "./themes/sportsbookcore.json";
import type { ThemeConfig, TextVariantValue } from "types/theme";
import {
  isThemePreviewMode,
  readThemePreviewConfig,
} from "utils/theme-preview-storage";

const textVariantDefaults = {
  landingTabLabel: { mobile: "base", desktop: "base" },
  sportTabLabel: { mobile: "sm", desktop: "sm" },
  categoryTabLabel: { mobile: "xs", desktop: "xs" },
  categorySubtypeLabel: { mobile: "xs", desktop: "xs" },
  chartTitle: { mobile: "sm", desktop: "xl" },
  chartValue: { mobile: "sm", desktop: "xl" },
  playerNameBall: { mobile: "xs", desktop: "sm" },
  statusMessageTitle: { mobile: "lg", desktop: "xl" },
  statusMessageDescription: { mobile: "lg", desktop: "base" },
  statusMessageBigTitle: { mobile: "lg", desktop: "xl" },
  timelineEventTitle: { mobile: "sm", desktop: "lg" },
  timelineEventDescription: { mobile: "xs", desktop: "base" },
  timelineTitle: { mobile: "sm", desktop: "lg" },
  timelineMinutePill: { mobile: "xs", desktop: "lg" },
  prematchInfoText: { mobile: "xs", desktop: "lg" },
  prematchKickoffText: { mobile: "xs", desktop: "lg" },
  prematchTeamName: { mobile: "xs", desktop: "xl" },
  prematchVsText: { mobile: "xxs", desktop: "base" },
  matchDetailsTitle: { mobile: "xxs", desktop: "xxs" },
  matchDetailsValue: { mobile: "xs", desktop: "base" },
  prematchTabLabel: { mobile: "xxs", desktop: "sm" },
  scoreboardText: { mobile: "xs", desktop: "lg" },
  scoreboardBaseballInning: { mobile: "xxs", desktop: "lg" },
  scoreboardBaseballCounts: { mobile: "xxs", desktop: "lg" },
  iceHockeyTimer: { mobile: "xxs", desktop: "lg" },
  tennisSets: { mobile: "xxs", desktop: "lg" },
  basketballTimer: { mobile: "xxs", desktop: "lg" },
  basketballFlashBannerTeamName: { mobile: "base", desktop: "xl" },
  basketballFlashBannerPoints: { mobile: "lg", desktop: "xxl" },
  basketballFlashBannerFooterText: { mobile: "base", desktop: "xl" },
  soccerTimer: { mobile: "xxs", desktop: "lg" },
  soccerChalkboardPlayerText: { mobile: "xs", desktop: "lg" },
  soccerInfoCardTitle: { mobile: "sm", desktop: "lg" },
  soccerInfoCardDescription: { mobile: "xs", desktop: "lg" },
  soccerScoreEffectText: { mobile: "sm", desktop: "lg" },
  soccerLineupTitle: { mobile: "sm", desktop: "lg" },
  soccerLineupPlayerName: { mobile: "sm", desktop: "lg" },
  volleyballScoreboard: { mobile: "xs", desktop: "lg" },
  volleyballSets: { mobile: "xxs", desktop: "lg" },
} as const satisfies Record<string, ResponsiveTextSize>;

export type TextVariant = keyof typeof textVariantDefaults;

type ColorPalette = {
  primary: string;
  primaryHover: string;
  border: string;
  yellow: string;
  simulatorBg: string;
  away: string;
  home: string;
  lightGreen: string;
  green: string;
  red: string;
  blue: string;
  darkestBlue: string;
  darkBlue: string;
  darkGray: string;
  lightGray: string;
  lighterGray: string;
  statisticTitle: string;
};

type FontConfig = {
  body: string;
  heading?: string;
};

type ComponentConfig = {
  sportTabsWrapperClass: string;
  sportTabsButtonBaseClass: string;
  sportTabsButtonHeightClass: string;
  sportTabsButtonActiveColorClass: string;
  sportTabsButtonInactiveColorClass: string;
  categoryTabsActiveClass: string;
  categoryTabsInactiveClass: string;
  categorySubtypesActiveClass: string;
  categorySubtypesInactiveClass: string;
};

type TextVariantOverride =
  | TextSize
  | {
      mobile?: TextSize;
      desktop?: TextSize;
    };

type ThemeFile = {
  name: string;
  colors: ColorPalette;
  fonts?: FontConfig;
  textVariants?: Partial<Record<string, TextVariantOverride>>;
  components?: Partial<ComponentConfig>;
};

type BrandTheme = {
  name: string;
  colors: ColorPalette;
  fonts: FontConfig;
  textVariants: Record<TextVariant, ResponsiveTextSize>;
  components: ComponentConfig;
};

const DEFAULT_FONT_CONFIG: FontConfig = {
  body: '"Inter", sans-serif',
  heading: '"Inter", sans-serif',
};

const DEFAULT_COMPONENT_CONFIG: ComponentConfig = {
  // The tab strip's box and each tab's shape. These defaults are the classes SportTabs.tsx
  // hard-coded before 2026-10-19, so every theme that does not set them renders exactly as before.
  sportTabsWrapperClass:
    "inline-flex w-full gap-2 p-2 overflow-x-auto overflow-y-hidden rounded-lg border border-border bg-darkestBlue",
  sportTabsButtonBaseClass:
    "tab-button inline-flex items-center justify-center rounded px-2 w-full font-semibold transition-all duration-300 ease-in-out text-base whitespace-nowrap",
  sportTabsButtonHeightClass: "h-10",
  sportTabsButtonActiveColorClass:
    "bg-yellow text-black border border-transparent",
  sportTabsButtonInactiveColorClass:
    "bg-transparent text-white border border-transparent",
  categoryTabsActiveClass: "border-yellow text-yellow",
  categoryTabsInactiveClass: "border-transparent text-white",
  categorySubtypesActiveClass: "bg-yellow text-black border-black",
  categorySubtypesInactiveClass: "bg-transparent text-border border-border",
};

const normalizeVariantOverride = (
  override: TextVariantOverride | undefined,
  fallback: ResponsiveTextSize
): ResponsiveTextSize => {
  if (!override) return fallback;
  if (typeof override === "string") {
    return (
      responsiveTextSizeDefaults[override] ??
      fallback ??
      DEFAULT_RESPONSIVE_TEXT_SIZE
    );
  }

  const mobile =
    override.mobile ?? fallback.mobile ?? DEFAULT_RESPONSIVE_TEXT_SIZE.mobile;
  const mobileDefaults =
    responsiveTextSizeDefaults[mobile] ?? DEFAULT_RESPONSIVE_TEXT_SIZE;

  const desktop: TextSize =
    override.desktop ??
    (override.mobile ? mobileDefaults.desktop : fallback.desktop);

  return {
    mobile,
    desktop,
  };
};

const coerceTheme = (theme: ThemeFile): BrandTheme => {
  const overrides = theme.textVariants ?? {};
  const normalizedVariants = Object.keys(textVariantDefaults).reduce(
    (acc, variantKey) => {
      const typedVariant = variantKey as TextVariant;
      const fallback =
        textVariantDefaults[typedVariant] ?? DEFAULT_RESPONSIVE_TEXT_SIZE;
      const override = overrides[variantKey] as TextVariantOverride | undefined;
      acc[typedVariant] = normalizeVariantOverride(override, fallback);
      return acc;
    },
    {} as Record<TextVariant, ResponsiveTextSize>
  );

  return {
    ...theme,
    fonts: { ...DEFAULT_FONT_CONFIG, ...theme.fonts },
    textVariants: normalizedVariants,
    components: { ...DEFAULT_COMPONENT_CONFIG, ...theme.components },
  };
};

const rawBrandThemes = {
  geniusfeed: coerceTheme(geniusfeedJson as ThemeFile),
  pixup: coerceTheme(pixupJson as ThemeFile),
  sportsbookcore: coerceTheme(sportsbookcoreJson as ThemeFile),
} as const satisfies Record<string, BrandTheme>;

const getThemeFallback = (brand?: string): BrandTheme => {
  if (brand && brand in rawBrandThemes) {
    return rawBrandThemes[brand as keyof typeof rawBrandThemes];
  }
  return rawBrandThemes[DEFAULT_BRAND] ?? rawBrandThemes.geniusfeed;
};

const normalizeVariantOverrides = (
  overrides?: Record<string, TextVariantValue>
): Partial<Record<string, TextVariantOverride>> => {
  if (!overrides) return {};
  return Object.entries(overrides).reduce((acc, [key, value]) => {
    if (!value) return acc;
    if (typeof value === "string") {
      acc[key] = value as TextVariantOverride;
    } else {
      acc[key] = { ...value } as TextVariantOverride;
    }
    return acc;
  }, {} as Partial<Record<string, TextVariantOverride>>);
};

const buildThemeFileFromConfig = (
  config: ThemeConfig,
  fallback: BrandTheme
): ThemeFile => ({
  name: config.name ?? fallback.name,
  colors: {
    ...fallback.colors,
    ...(config.colors ?? {}),
  } as ColorPalette,
  fonts: {
    ...fallback.fonts,
    ...(config.fonts ?? {}),
  },
  components: {
    ...fallback.components,
    ...(config.components ?? {}),
  },
  textVariants: normalizeVariantOverrides(config.textVariants),
});

const brandThemes = rawBrandThemes;

export type BrandId = keyof typeof brandThemes;
export type { BrandTheme, FontConfig };

const DEFAULT_BRAND: BrandId = "geniusfeed";
const envBrand = process.env.REACT_APP_BRAND as BrandId | undefined;

const isBrandId = (brand?: string): brand is BrandId =>
  Boolean(brand && brand in brandThemes);

// Runtime theme, for an embedder that asks for one: `?theme=sportsbookcore[&accent=ff174f]`.
// Only a known brand id is honoured, and only the embed that sends it changes: every other
// embedder (no `theme` parameter) keeps the build's brand exactly as before.
const readQuery = (key: string): string | null => {
  if (typeof window === "undefined") return null;
  try {
    return new URLSearchParams(window.location.search).get(key);
  } catch {
    return null;
  }
};
const runtimeBrand = readQuery("theme") ?? undefined;
const runtimeAccent = (readQuery("accent") ?? "").replace(/^#/, "");
// true only for an embedder that opted in with ?theme=; such an embedder also listens for our height.
export const isRuntimeThemed = isBrandId(runtimeBrand);

export const activeBrandId: BrandId = isBrandId(runtimeBrand)
  ? runtimeBrand
  : isBrandId(envBrand)
  ? envBrand
  : DEFAULT_BRAND;

// The accent follows the embedding sportsbook's own action colour (6 hex digits), so the
// tracker matches each brand: highlights, active tabs and chips take it.
const lighten = (hex: string, amount: number): string => {
  const n = parseInt(hex, 16);
  const mix = (c: number) => Math.round(c + (255 - c) * amount);
  const r = mix((n >> 16) & 255);
  const g = mix((n >> 8) & 255);
  const b = mix(n & 255);
  return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1)}`;
};
const withAccent = (theme: BrandTheme): BrandTheme => {
  if (!isBrandId(runtimeBrand) || !/^[0-9a-f]{6}$/i.test(runtimeAccent)) {
    return theme;
  }
  const accent = `#${runtimeAccent}`;
  return {
    ...theme,
    colors: {
      ...theme.colors,
      primary: accent,
      primaryHover: lighten(runtimeAccent, 0.2),
      yellow: accent,
    },
  };
};

const previewPayload = isThemePreviewMode() ? readThemePreviewConfig() : null;
const previewTheme = previewPayload?.config
  ? coerceTheme(
      buildThemeFileFromConfig(
        previewPayload.config,
        getThemeFallback(previewPayload.customer)
      )
    )
  : null;

export const activeTheme: BrandTheme =
  previewTheme ?? withAccent(brandThemes[activeBrandId]);
export const availableBrands = brandThemes;
export const fontConfig = activeTheme.fonts;
export const componentConfig = activeTheme.components;

export const resolveTextSize = ({
  size,
  variant,
}: {
  size?: TextSize;
  variant?: TextVariant;
} = {}): ResponsiveTextSize => {
  if (size) {
    return responsiveTextSizeDefaults[size] ?? DEFAULT_RESPONSIVE_TEXT_SIZE;
  }
  if (variant) {
    return (
      activeTheme.textVariants[variant] ??
      textVariantDefaults[variant] ??
      DEFAULT_RESPONSIVE_TEXT_SIZE
    );
  }
  return DEFAULT_RESPONSIVE_TEXT_SIZE;
};

export const colorPalette = activeTheme.colors;
